import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeName = 'light' | 'dark' | 'system';

interface ThemeContextValue {
    theme: ThemeName;
    resolvedTheme: 'light' | 'dark';
    setTheme: (theme: ThemeName) => void;
    toggleTheme: () => void;
}

const THEME_STORAGE_KEY = 'arrowise-theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
    children: React.ReactNode;
    defaultTheme?: ThemeName;
    enableSystem?: boolean;
    /** base color family name, e.g. slate, zinc, gray; used in data-theme attr */
    colorScheme?: string;
}

function getSystemTheme(): 'light' | 'dark' {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({
    children,
    defaultTheme = 'light',
    enableSystem = true,
    colorScheme = 'slate'
}: ThemeProviderProps) {
    const [theme, setThemeState] = useState<ThemeName>(() => {
        if (typeof window === 'undefined') return defaultTheme;
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeName | null;
        return stored || defaultTheme;
    });
    const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => getSystemTheme());

    useEffect(() => {
        if (!enableSystem) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const listener = () => setSystemTheme(mq.matches ? 'dark' : 'light');
        mq.addEventListener('change', listener);
        return () => mq.removeEventListener('change', listener);
    }, [enableSystem]);

    const resolvedTheme: 'light' | 'dark' = theme === 'system' ? systemTheme : theme;

    const applyThemeAttributes = useCallback((next: 'light' | 'dark') => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        root.setAttribute('data-theme', colorScheme);
        root.classList.toggle('dark', next === 'dark');
    }, [colorScheme]);

    useEffect(() => {
        applyThemeAttributes(resolvedTheme);
    }, [resolvedTheme, applyThemeAttributes]);

    const setTheme = useCallback((next: ThemeName) => {
        setThemeState(next);
        try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch { }
    }, []);

    const toggleTheme = useCallback(() => {
        let next: ThemeName;
        if (theme === 'system') {
            next = resolvedTheme === 'dark' ? 'light' : 'dark';
        } else {
            next = theme === 'dark' ? 'light' : 'dark';
        }
        setTheme(next);
    }, [theme, resolvedTheme, setTheme]);

    const value = useMemo<ThemeContextValue>(() => ({ theme, resolvedTheme, setTheme, toggleTheme }), [theme, resolvedTheme, setTheme, toggleTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
    return ctx;
}

export function ThemeToggleButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    const { resolvedTheme, toggleTheme } = useTheme();
    return (
        <button type="button" onClick={toggleTheme} aria-label="Toggle theme" {...props}>
            {resolvedTheme === 'dark' ? '🌙' : '☀️'}
        </button>
    );
}
